/**
 * Pure helpers for computing custom period selections and building the
 * period-inquiry request body.
 *
 * Ported from apps/mockups/src/screens/components/client-offer/period-selection-utils.ts
 * Traceability: client-offer-view-completion / Task 4.3
 */
import type { OfferPeriodInquiryUnitSelection } from '@ooh/contracts';

/**
 * Returns true if any unit's selected periods differ from its defaults.
 * An empty selection that matches an empty default is NOT considered custom.
 */
export function hasCustomPeriodSelection(
  selected: Record<string, string[]>,
  defaults: Record<string, string[]>,
): boolean {
  return Object.entries(selected).some(([unitId, periodIds]) => {
    if (periodIds.length === 0) return false;
    const defaultIds = defaults[unitId] ?? [];
    return (
      periodIds.length !== defaultIds.length ||
      periodIds.some((periodId) => !defaultIds.includes(periodId))
    );
  });
}

/**
 * Build the inquiry request body from the current per-unit selected periods.
 * Only includes units with at least one selected period.
 */
export function buildInquiryBody(
  selectedPeriods: Record<string, string[]>,
): OfferPeriodInquiryUnitSelection[] {
  return Object.entries(selectedPeriods)
    .filter(([, periodIds]) => periodIds.length > 0)
    .map(([unitId, periodIds]) => ({ unitId, periodIds }));
}
